import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { notify } from '../../../core/notifications/notify';
import { ConfirmationModal } from '../../../components';
import {
  EmissionFactor,
  EmissionFactorSource,
  EmissionFactorCategory,
  EmissionFactorCalculation,
  EmissionFactorSearchOptions,
  CreateEmissionFactorRequest,
  UpdateEmissionFactorRequest,
  CalculateEmissionsRequest
} from '../../../../../shared/types/emissionFactors';
import { emissionFactorsApi } from '../api/emissionFactors.api';
import {
  Database,
  Search,
  Plus,
  Download,
  Upload,
  Calculator,
  CheckCircle,
  X,
  RefreshCw,
  AlertTriangle,
  Target,
  BarChart3,
  Globe,
  Eye,
  Edit
} from 'lucide-react';

const EmissionFactorsManager: React.FC = () => {
  const { hasPermission } = useAuth();
  const [categories, setCategories] = useState<EmissionFactorCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<EmissionFactorCategory | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [factors, setFactors] = useState<EmissionFactor[]>([]);
  const [dataSources, setDataSources] = useState<EmissionFactorSource[]>([]);
  const [searchResults, setSearchResults] = useState<EmissionFactor[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddFactorModal, setShowAddFactorModal] = useState(false);
  const [showEditFactorModal, setShowEditFactorModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [calculationResult, setCalculationResult] = useState<EmissionFactorCalculation | null>(null);
  const [selectedFactor, setSelectedFactor] = useState<EmissionFactor | null>(null);

  // Form states
  const [searchOptions, setSearchOptions] = useState<EmissionFactorSearchOptions>({
    category: '',
    source: '',
    scope: '',
    unit: '',
    search: ''
  });

  const [newFactor, setNewFactor] = useState<Partial<CreateEmissionFactorRequest>>({
    source: 'DEFRA',
    category: '',
    subcategory: '',
    factor: 0,
    unit: 'kgCO2e/unit',
    description: '',
    scope: 'scope1'
  });

  const [calculation, setCalculation] = useState({
    activity: 0,
    emissionFactorId: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [loadedCategories, loadedDataSources] = await Promise.all([
        emissionFactorsApi.getEmissionFactorCategories(),
        emissionFactorsApi.getEmissionFactorSources()
      ]);

      setCategories(loadedCategories);
      setDataSources(loadedDataSources);

      // Select first category by default
      if (loadedCategories.length > 0 && loadedCategories[0]) {
        setSelectedCategory(loadedCategories[0]);
        const firstSubcategoryId = loadedCategories[0].subcategories[0]?.id || null;
        setSelectedSubcategory(firstSubcategoryId);
        if (firstSubcategoryId) {
          loadFactors(firstSubcategoryId);
        }
      }
    } catch (error) {
      console.error('Failed to load emission factors data:', error);
      notify.error('Failed to load emission factors data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadFactors = useCallback(async (subcategoryId: string) => {
    setIsLoading(true);
    try {
      const loadedFactors = await emissionFactorsApi.getEmissionFactorsByCategory(subcategoryId);
      setFactors(loadedFactors);
    } catch (error) {
      console.error('Failed to load factors:', error);
      setFactors([]);
      notify.error('Failed to load emission factors');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleCategoryChange = (category: EmissionFactorCategory) => {
    setSelectedCategory(category);
    const firstSubcategoryId = category.subcategories[0]?.id || null;
    setSelectedSubcategory(firstSubcategoryId);
    if (firstSubcategoryId) {
      loadFactors(firstSubcategoryId);
    }
  };

  const handleSubcategoryChange = (subcategoryId: string) => {
    setSelectedSubcategory(subcategoryId);
    loadFactors(subcategoryId);
  };

  const handleSearch = useCallback(async () => {
    setIsSearching(true);
    try {
      const results = await emissionFactorsApi.getEmissionFactors(searchOptions);
      setSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
      notify.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  }, [searchOptions]);

  const handleClearSearch = () => {
    setSearchOptions({
      category: '',
      source: '',
      scope: '',
      unit: '',
      search: ''
    });
    setSearchResults([]);
    setIsSearching(false);
  };

  const handleAddFactor = useCallback(async () => {
    if (!newFactor.category || !newFactor.subcategory || !newFactor.factor || !newFactor.unit) {
      notify.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const factorRequest: CreateEmissionFactorRequest = {
        name: newFactor.category || '',
        description: newFactor.description || '',
        category: newFactor.category || '',
        subcategory: newFactor.subcategory || '',
        factor: newFactor.factor || 0,
        unit: newFactor.unit || 'kgCO2e/unit',
        source: newFactor.source || 'DEFRA',
        scope: newFactor.scope || 'scope1'
      };

      await emissionFactorsApi.createEmissionFactor(factorRequest);
      loadFactors(selectedSubcategory || '');
      setShowAddFactorModal(false);
      setNewFactor({
        source: 'DEFRA',
        category: '',
        subcategory: '',
        factor: 0,
        unit: 'kgCO2e/unit',
        description: '',
        scope: 'scope1'
      });
      notify.success('Emission factor added successfully');
    } catch (error) {
      console.error('Failed to add emission factor:', error);
      notify.error('Failed to add emission factor');
    } finally {
      setIsLoading(false);
    }
  }, [newFactor, selectedSubcategory, loadFactors]);

  const handleCalculate = useCallback(async () => {
    if (!calculation.activity || !calculation.emissionFactorId) {
      notify.error('Please fill in activity amount and select an emission factor');
      return;
    }

    setIsLoading(true);
    try {
      const request: CalculateEmissionsRequest = {
        emissionFactorId: calculation.emissionFactorId,
        activityData: calculation.activity,
        unit: 'kgCO2e'
      };

      const result = await emissionFactorsApi.calculateEmissions(request);
      setCalculationResult({
        id: Date.now(),
        emissionFactorId: result.emissionFactor.id,
        emissionFactorName: result.emissionFactor.name,
        activityData: result.activityData,
        unit: result.unit,
        calculatedEmissions: result.calculatedEmissions,
        calculatedAt: result.calculatedAt
      });
      setShowCalculationModal(true);
    } catch (error) {
      console.error('Calculation failed:', error);
      notify.error('Calculation failed');
    } finally {
      setIsLoading(false);
    }
  }, [calculation]);

  const handleExport = useCallback(() => {
    try {
      const jsonData = JSON.stringify(factors, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `emission_factors_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify.success('Emission factors exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      notify.error('Export failed');
    }
  }, [factors]);

  const handleEditFactor = (factor: EmissionFactor) => {
    setSelectedFactor(factor);
    setShowEditFactorModal(true);
  };

  const handleSaveEditedFactor = useCallback(async () => {
    if (!selectedFactor) return;

    setIsLoading(true);
    try {
      const updateRequest: UpdateEmissionFactorRequest = {
        name: selectedFactor.name,
        description: selectedFactor.description || '',
        factor: selectedFactor.factor,
        unit: selectedFactor.unit,
        source: selectedFactor.source,
        isActive: selectedFactor.isActive
      };

      await emissionFactorsApi.updateEmissionFactor(selectedFactor.id, updateRequest);
      loadFactors(selectedSubcategory || '');
      setShowEditFactorModal(false);
      setSelectedFactor(null);
      notify.success('Emission factor updated successfully');
    } catch (error) {
      console.error('Failed to update emission factor:', error);
      notify.error('Failed to update emission factor');
    } finally {
      setIsLoading(false);
    }
  }, [selectedFactor, selectedSubcategory, loadFactors]);

  const handleDeleteFactor = useCallback(async () => {
    if (!selectedFactor) return;

    setIsLoading(true);
    try {
      await emissionFactorsApi.deleteEmissionFactor(selectedFactor.id);
      loadFactors(selectedSubcategory || '');
      setShowDeleteConfirmModal(false);
      setSelectedFactor(null);
      notify.success('Emission factor deleted successfully');
    } catch (error) {
      console.error('Failed to delete emission factor:', error);
      notify.error('Failed to delete emission factor');
    } finally {
      setIsLoading(false);
    }
  }, [selectedFactor, selectedSubcategory, loadFactors]);

  const handleImport = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const jsonData = e.target?.result as string;
        const importedFactors = JSON.parse(jsonData) as EmissionFactor[];

        for (const factor of importedFactors) {
          const createRequest: CreateEmissionFactorRequest = {
            name: factor.name,
            description: factor.description || '',
            category: factor.category,
            subcategory: factor.subcategory,
            factor: factor.factor,
            unit: factor.unit,
            source: factor.source,
            scope: factor.scope
          };
          await emissionFactorsApi.createEmissionFactor(createRequest);
        }

        loadData();
        notify.success('Emission factors imported successfully');
      } catch (error) {
        console.error('Import failed:', error);
        notify.error('Import failed');
      }
    };
    reader.readAsText(file);
  }, [loadData]);



  const getSourceColor = (source: string) => {
    const colors: Record<string, string> = {
      'DEFRA': 'bg-green-100 text-green-800',
      'EPA': 'bg-blue-100 text-blue-800',
      'IEA': 'bg-purple-100 text-purple-800'
    };
    return colors[source] || 'bg-gray-100 text-gray-800';
  };

  if (!hasPermission('compliance.read') && !hasPermission('templates.update')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-red-600 px-8 py-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <AlertTriangle className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Access Denied</h2>
                <p className="text-red-100 text-sm mt-0.5">Restricted Area</p>
              </div>
            </div>
          </div>
          <div className="p-8">
            <p className="text-slate-600">Only Super Admin and Compliance Admin can access Emission Factors Management.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="bg-emerald-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Database className="text-white" size={32} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Emission Factors Database</h1>
                  <p className="text-emerald-100 mt-1">Manage DEFRA, EPA, and IEA emission factors for accurate carbon calculations</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowAddFactorModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-emerald-600 font-medium rounded-xl hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-emerald-600 transition-all shadow-lg"
                >
                  <Plus size={20} />
                  Add Factor
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Data Sources */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Globe className="text-slate-600" size={24} />
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Data Sources</h2>
              <p className="text-sm text-slate-600">Official emission factor databases</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dataSources.map((source) => (
              <div key={source.id} className="p-4 border border-slate-200 rounded-xl hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900">{source.name}</h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(source.name)}`}>
                    {source.version}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-3">{source.description}</p>
                <div className="space-y-1 text-xs text-slate-500">
                  <p>Updated: {new Date(source.lastUpdated).toLocaleDateString()}</p>
                  {source.url && (
                    <a href={source.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium">
                      <Eye size={12} />
                      View Source
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Search className="text-slate-600" size={24} />
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Search Emission Factors</h2>
              <p className="text-sm text-slate-600">Find specific emission factors by criteria</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Category</label>
              <select
                value={searchOptions.category}
                onChange={(e) => setSearchOptions((prev: EmissionFactorSearchOptions) => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  cat.subcategories.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Source</label>
              <select
                value={searchOptions.source}
                onChange={(e) => setSearchOptions((prev: EmissionFactorSearchOptions) => ({ ...prev, source: e.target.value }))}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
              >
                <option value="">All Sources</option>
                {dataSources.map(source => (
                  <option key={source.id} value={source.name}>{source.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Scope</label>
              <select
                value={searchOptions.scope}
                onChange={(e) => setSearchOptions((prev: EmissionFactorSearchOptions) => ({ ...prev, scope: e.target.value }))}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
              >
                <option value="">All Scopes</option>
                <option value="scope1">Scope 1</option>
                <option value="scope2">Scope 2</option>
                <option value="scope3">Scope 3</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Unit</label>
              <input
                type="text"
                value={searchOptions.unit}
                onChange={(e) => setSearchOptions((prev: EmissionFactorSearchOptions) => ({ ...prev, unit: e.target.value }))}
                placeholder="e.g., kWh, litre"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Search</label>
              <input
                type="text"
                value={searchOptions.search}
                onChange={(e) => setSearchOptions((prev: EmissionFactorSearchOptions) => ({ ...prev, search: e.target.value }))}
                placeholder="Keyword search"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {isSearching ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search size={18} />
                    Search
                  </>
                )}
              </button>
              <button
                onClick={handleClearSearch}
                className="px-4 py-3 bg-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Categories and Factors */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Categories */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <Target className="text-slate-600" size={24} />
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Categories</h2>
                    <p className="text-sm text-slate-600">Emission categories</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-2">
                  {categories.map((category) => (
                    <div key={category.id}>
                      <button
                        onClick={() => handleCategoryChange(category)}
                        className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                          selectedCategory?.id === category.id
                            ? 'bg-emerald-100 text-emerald-800 shadow-sm'
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{category.name}</span>
                        </div>
                      </button>
                      {selectedCategory?.id === category.id && (
                        <div className="ml-6 mt-2 space-y-1">
                          {category.subcategories.map((subcategory) => (
                            <button
                              key={subcategory.id}
                              onClick={() => handleSubcategoryChange(subcategory.id)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all ${
                                selectedSubcategory === subcategory.id
                                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                                  : 'text-slate-600 hover:bg-slate-50'
                              }`}
                            >
                              {subcategory.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Factors */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="text-slate-600" size={24} />
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">
                        {isSearching ? 'Search Results' : 'Emission Factors'}
                      </h2>
                      <p className="text-sm text-slate-600">Carbon emission conversion factors</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleExport}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-sm"
                    >
                      <Download size={18} />
                      Export
                    </button>
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all shadow-sm cursor-pointer">
                      <Upload size={18} />
                      Import
                      <input
                        type="file"
                        accept=".json"
                        onChange={handleImport}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <RefreshCw className="text-emerald-600 mb-4 animate-spin" size={64} />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">Loading...</h3>
                    <p className="text-slate-600">Please wait while we load the emission factors</p>
                  </div>
                ) : (isSearching ? searchResults : factors).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Database className="text-slate-400 mb-4" size={64} />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {isSearching ? 'No results found' : 'No emission factors'}
                    </h3>
                    <p className="text-slate-600 max-w-md">
                      {isSearching ? 'Try adjusting your search criteria' : 'No emission factors available for this category'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-100">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Source</th>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Factor</th>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Unit</th>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Description</th>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Updated</th>
                          <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-100">
                        {(isSearching ? searchResults : factors).map((factor) => (
                          <tr key={factor.id} className="hover:bg-slate-50">
                            <td className="px-6 py-4 whitespace-nowrap text-left">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSourceColor(factor.source)}`}>
                                {factor.source}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900">
                              {factor.category}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900 font-mono">
                              {typeof factor.factor === 'number' ? factor.factor.toFixed(6) : factor.factor}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900">
                              {factor.unit}
                            </td>
                            <td className="px-6 py-4 text-left text-sm text-slate-900 max-w-xs truncate">
                              {factor.description || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900">
                              {new Date(factor.updatedAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    setCalculation({
                                      activity: 100,
                                      emissionFactorId: factor.id
                                    });
                                    handleCalculate();
                                  }}
                                  className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all"
                                >
                                  <Calculator size={14} />
                                  Calculate
                                </button>
                                <button
                                  onClick={() => handleEditFactor(factor)}
                                  className="inline-flex items-center gap-1 px-3 py-1 bg-slate-600 text-white text-xs font-medium rounded-lg hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
                                >
                                  <Edit size={14} />
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedFactor(factor);
                                    setShowDeleteConfirmModal(true);
                                  }}
                                  className="inline-flex items-center gap-1 px-3 py-1 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all"
                                >
                                  <X size={14} />
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add Factor Modal */}
        {showAddFactorModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
            <div className="relative min-h-full flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
                {/* Modal Header */}
                <div className="bg-emerald-600 px-8 py-6 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                        <Plus className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Add Emission Factor</h3>
                        <p className="text-emerald-100 text-sm mt-0.5">Create a new emission conversion factor</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowAddFactorModal(false)}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-colors"
                    >
                      <X className="text-white" size={24} />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-8 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Source</label>
                      <select
                        value={newFactor.source}
                        onChange={(e) => setNewFactor((prev: Partial<CreateEmissionFactorRequest>) => ({ ...prev, source: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                      >
                        {dataSources.map(source => (
                          <option key={source.id} value={source.name}>{source.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Scope</label>
                      <select
                        value={newFactor.scope}
                        onChange={(e) => setNewFactor((prev: Partial<CreateEmissionFactorRequest>) => ({ ...prev, scope: e.target.value as 'scope1' | 'scope2' | 'scope3' }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                      >
                        <option value="scope1">Scope 1</option>
                        <option value="scope2">Scope 2</option>
                        <option value="scope3">Scope 3</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Category *</label>
                      <input
                        type="text"
                        value={newFactor.category}
                        onChange={(e) => setNewFactor((prev: Partial<CreateEmissionFactorRequest>) => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                        placeholder="e.g., Electricity Grid"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Subcategory *</label>
                      <input
                        type="text"
                        value={newFactor.subcategory}
                        onChange={(e) => setNewFactor((prev: Partial<CreateEmissionFactorRequest>) => ({ ...prev, subcategory: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                        placeholder="e.g., Grid Mix"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Factor Value *</label>
                      <input
                        type="number"
                        step="0.000001"
                        value={newFactor.factor}
                        onChange={(e) => setNewFactor((prev: Partial<CreateEmissionFactorRequest>) => ({ ...prev, factor: parseFloat(e.target.value) }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                        placeholder="e.g., 0.233"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Unit *</label>
                      <input
                        type="text"
                        value={newFactor.unit}
                        onChange={(e) => setNewFactor((prev: Partial<CreateEmissionFactorRequest>) => ({ ...prev, unit: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                        placeholder="e.g., kgCO2e/kWh"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Description</label>
                    <textarea
                      value={newFactor.description}
                      onChange={(e) => setNewFactor((prev: Partial<CreateEmissionFactorRequest>) => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="Describe this emission factor..."
                    />
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-8 py-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
                  <button
                    onClick={() => setShowAddFactorModal(false)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddFactor}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {isLoading ? (
                      <>
                        <RefreshCw size={18} className="animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Add Factor
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calculation Result Modal */}
        {showCalculationModal && calculationResult && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
            <div className="relative min-h-full flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
                {/* Modal Header */}
                <div className="bg-blue-600 px-8 py-6 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                        <Calculator className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Emission Calculation Result</h3>
                        <p className="text-blue-100 text-sm mt-0.5">Carbon footprint calculation breakdown</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowCalculationModal(false)}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-colors"
                    >
                      <X className="text-white" size={24} />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-8 space-y-6">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-emerald-700">Activity Amount:</p>
                        <p className="font-semibold text-emerald-900">{calculationResult.activityData}</p>
                      </div>
                      <div>
                        <p className="text-sm text-emerald-700">Emission Factor:</p>
                        <p className="font-semibold text-emerald-900">{calculationResult.emissionFactorName}</p>
                      </div>
                    </div>
                    <div className="border-t border-emerald-200 pt-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-emerald-900">Total CO2e:</span>
                        <span className="text-2xl font-bold text-emerald-600">
                          {calculationResult.calculatedEmissions.toFixed(4)} {calculationResult.unit}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-8 py-6 border-t border-slate-200 bg-slate-50 flex justify-end">
                  <button
                    onClick={() => setShowCalculationModal(false)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-600 text-white font-medium rounded-xl hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all shadow-sm"
                  >
                    <CheckCircle size={18} />
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Factor Modal */}
        {showEditFactorModal && selectedFactor && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
            <div className="relative min-h-full flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
                {/* Modal Header */}
                <div className="bg-slate-700 px-8 py-6 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                        <Edit className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Edit Emission Factor</h3>
                        <p className="text-slate-100 text-sm mt-0.5">Modify the selected emission factor</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowEditFactorModal(false);
                        setSelectedFactor(null);
                      }}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-colors"
                    >
                      <X className="text-white" size={24} />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-8 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Source</label>
                      <select
                        value={selectedFactor.source}
                        onChange={(e) => setSelectedFactor((prev: EmissionFactor | null) => prev ? { ...prev, source: e.target.value } : null)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                      >
                        {dataSources.map(source => (
                          <option key={source.id} value={source.name}>{source.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Category *</label>
                      <input
                        type="text"
                        value={selectedFactor.category}
                        onChange={(e) => setSelectedFactor((prev: EmissionFactor | null) => prev ? { ...prev, category: e.target.value } : null)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                        placeholder="e.g., Electricity Grid"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Factor Value *</label>
                      <input
                        type="number"
                        step="0.000001"
                        value={selectedFactor.factor}
                        onChange={(e) => setSelectedFactor((prev: EmissionFactor | null) => prev ? { ...prev, factor: parseFloat(e.target.value) } : null)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                        placeholder="e.g., 0.233"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Unit *</label>
                      <input
                        type="text"
                        value={selectedFactor.unit}
                        onChange={(e) => setSelectedFactor((prev: EmissionFactor | null) => prev ? { ...prev, unit: e.target.value } : null)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                        placeholder="e.g., kgCO2e/kWh"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">Description</label>
                    <textarea
                      value={selectedFactor.description}
                      onChange={(e) => setSelectedFactor((prev: EmissionFactor | null) => prev ? { ...prev, description: e.target.value } : null)}
                      rows={3}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                      placeholder="Describe this emission factor..."
                    />
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-8 py-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowEditFactorModal(false);
                      setSelectedFactor(null);
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEditedFactor}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {isLoading ? (
                      <>
                        <RefreshCw size={18} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirmModal}
          onClose={() => {
            setShowDeleteConfirmModal(false);
            setSelectedFactor(null);
          }}
          onConfirm={handleDeleteFactor}
          title="Delete Emission Factor"
          message={`Are you sure you want to delete emission factor "${selectedFactor?.name}"? This action cannot be undone.`}
          confirmText="Delete Factor"
          cancelText="Cancel"
          type="danger"
        >
          {selectedFactor && (
            <div>
              <h5 className="text-sm font-medium text-slate-900 mb-2">Factor Details:</h5>
              <div className="space-y-1 text-sm text-slate-700">
                <p><strong>Source:</strong> {selectedFactor.source}</p>
                <p><strong>Category:</strong> {selectedFactor.category}</p>
                <p><strong>Factor:</strong> {typeof selectedFactor.factor === 'number' ? selectedFactor.factor.toFixed(6) : selectedFactor.factor} {selectedFactor.unit}</p>
              </div>
            </div>
          )}
        </ConfirmationModal>
      </div>
    </div>
  );
};

export default EmissionFactorsManager;
